import React from "react";

import Box from "veneer/Box";
import { IconButton } from "veneer/IconButton";
import { DownloadIcon, ExpandMapIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import {
  isImage,
  isUserContent,
  isVideo,
} from "cortex/ui/src/fleet/view/media-library/utils";
import { download } from "cortex/ui/src/util/download";
import webPageIcon from "cortex/ui/static/svg/icon-webpage.svg";

interface ContentPreviewProps {
  hideFullscreen?: () => void;
  mimeType?: string | null;
  name?: string;
  showFullscreen?: (
    url: string,
    mediaType: "image" | "video" | null,
    filename: string | undefined,
  ) => void;
  type?: string;
  url?: string;
}

const clearIconButtonStyle = {
  background: Colors.white30,
  color: Colors.black,

  "&:hover": {
    background: Colors.white40,
  },
};

export const ClearIconButton = styled(IconButton)(clearIconButtonStyle);

const ContentPreviewVideo = styled("video")({
  maxHeight: "380px",
  maxWidth: "100%",
});

const ContentPreviewRoot = styled(Box)({
  backgroundColor: Colors.gray200,
  maxHeight: "380px",
  position: "relative",
  textAlign: "center",
});

const ContentPreviewMedia = styled("img")({
  maxHeight: "380px",
  maxWidth: "100%",
});

const ContentPreviewControls = styled(Box)(({ theme }) => ({
  alignSelf: "start",
  margin: theme.spacing(1),
  position: "absolute",
  right: 0,
  zIndex: 1,
}));

const NoPreview = styled(Box)({
  alignItems: "center",
  display: "flex",
  fontSize: "x-large",
  height: 60,
  justifyContent: "center",
});

const IconPreview = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  width: 120,
}));

function getFullscreenType(mimeType?: string | null): "image" | "video" | null {
  if (isImage(mimeType)) {
    return "image";
  }

  if (isVideo(mimeType)) {
    return "video";
  }

  return null;
}

function renderNoPreview() {
  return <NoPreview>No Preview</NoPreview>;
}

function renderImage(url: string, name?: string) {
  return <ContentPreviewMedia alt={name ?? ""} src={url} />;
}

function renderIconPreview(url: string) {
  return <IconPreview alt="" src={url} />;
}

function renderContentPreview({
  mimeType,
  name,
  url,
}: Pick<ContentPreviewProps, "mimeType" | "name" | "url">) {
  if (mimeType === MEDIA_ENTRY_WEBPAGE) {
    return renderIconPreview(webPageIcon);
  }

  if (mimeType?.startsWith("image") && url) {
    return renderImage(url, name);
  }

  if (mimeType?.startsWith("video") && url) {
    // Force video element to rerender.
    // https://stackoverflow.com/a/47382850/1870013
    return (
      <ContentPreviewVideo controls={true} key={url} preload="none">
        <source src={url} />
      </ContentPreviewVideo>
    );
  }

  return renderNoPreview();
}

function renderPreview({ mimeType, name, type, url }: ContentPreviewProps) {
  if (type === MEDIA_ENTRY_WEBPAGE) {
    return renderIconPreview(webPageIcon);
  }

  if (!url) {
    return renderNoPreview();
  }

  if (type === MEDIA_ENTRY_USER_CONTENT) {
    return renderContentPreview({ mimeType, name, url });
  }

  if (type === MEDIA_ENTRY_APPLICATION) {
    return renderImage(url, name);
  }

  return renderImage(url, name);
}

export function ContentPreview({
  mimeType,
  name,
  showFullscreen = () => {},
  type,
  url,
}: ContentPreviewProps) {
  const disabled = !url || !isUserContent(type);

  const viewFullscreen = () => {
    if (!url) {
      return;
    }

    showFullscreen(url, getFullscreenType(mimeType), name);
  };

  const downloadContent = () => {
    if (!url) {
      return;
    }

    download(url, name);
  };

  return (
    <ContentPreviewRoot dataTestId="content-preview" mb={2}>
      <ContentPreviewControls>
        <ClearIconButton
          dataTestId="content-preview-fullscreen"
          disabled={disabled}
          icon={<ExpandMapIcon />}
          onClick={viewFullscreen}
          title="View Fullscreen"
        />
        <ClearIconButton
          dataTestId="content-preview-download"
          disabled={disabled}
          icon={<DownloadIcon />}
          onClick={downloadContent}
          title="Download"
        />
      </ContentPreviewControls>
      {renderPreview({ mimeType, name, type, url })}
    </ContentPreviewRoot>
  );
}
